import React, { useEffect, useRef, useState, useCallback, memo, forwardRef } from 'react';
import styled from 'styled-components';
import FallbackStarMap from './FallbackStarMap';

// Note: d3-celestial is now loaded directly in public/index.html

const StarMapContainer = styled.div.attrs({ className: 'starmap-container' })`
  width: 100%;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #000;
  border-radius: 8px;
  overflow: visible;
  max-width: 100%;
  padding: 0;
  box-sizing: border-box;
  margin: 0 auto;
`;

const MapFrame = styled.div.attrs({ className: 'starmap-frame' })`
  width: 100%;
  height: 0;
  padding-bottom: 100%; /* Maintain 1:1 aspect ratio */
  position: relative;
  border: none;
  background-color: #000;
  box-sizing: border-box;
  overflow: hidden;
  border-radius: 50%;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.5);
`;

const MapCanvas = styled.div.attrs({ className: 'starmap-canvas', id: 'celestial-map-container' })`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  
  /* Ensure the d3-celestial canvas is properly sized */
  & canvas {
    width: 100% !important;
    height: 100% !important;
    border-radius: 50%;
    overflow: hidden;
    object-fit: contain;
  }
  
  /* Hide zoom controls */
  & #celestial-zoomin,
  & #celestial-zoomout {
    display: none !important;
  }
`;

const CaptionSection = styled.div.attrs({ className: 'starmap-caption-section' })`
  width: 100%;
  padding: 0 10px;
  margin: 0;
  background-color: transparent;
  color: white;
  text-align: center;
  font-family: 'Montserrat', sans-serif;
  position: relative;
  z-index: 10; /* Ensure this appears above other elements */
  max-width: 90%; /* Match the width of the map */
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const MainCaption = styled.div.attrs({ className: 'starmap-main-caption' })`
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 5px;
`;

const LocationDate = styled.div.attrs({ className: 'starmap-location-date' })`
  font-size: 16px;
  opacity: 0.9;
  line-height: 1.5;
  letter-spacing: 0.5px;
  margin-top: 5px;
`;

const ErrorMessage = styled.div.attrs({ className: 'starmap-error-message' })`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 8px;
  background-color: rgba(0, 0, 0, 0.7);
  color: #ccc;
  font-size: 12px;
  text-align: center;
  opacity: 0.8;
  transition: opacity 0.3s ease;
`;

// Helper function to check if celestial is fully loaded
const isCelestialReady = () => {
    return window.Celestial && window.Celestial.display;
};

// Format date for display
const formatDate = (dateString) => {
    if (!dateString) return '';

    try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
        });
    } catch (e) {
        return '';
    }
};

// Format location for display
const formatLocation = (location) => {
    if (!location) return '';

    try {
        let lat, lon;
        if (typeof location === 'string') {
            // Handle string format like "lat,lon"
            [lat, lon] = location.split(',').map(n => parseFloat(n.trim()));
        } else if (location.latitude !== undefined && location.longitude !== undefined) {
            // Handle object format {latitude, longitude}
            lat = parseFloat(location.latitude);
            lon = parseFloat(location.longitude);
        } else {
            return '';
        }

        return `${Math.abs(lat).toFixed(2)}° ${lat >= 0 ? 'N' : 'S'}, ${Math.abs(lon).toFixed(2)}° ${lon >= 0 ? 'E' : 'W'}`;
    } catch (e) {
        return '';
    }
};

const StarMapGenerator = forwardRef(({
    date,
    location,
    size,
    caption,
    mapStyle,
    showStars = true,
    showConstellations = true,
    showConstellationNames = true,
    showGrid = true,
    showDeepSkyObjects = true,
    showEcliptic = true,
    onError,
    onLoaded,
    regenerate = true
}, ref) => {
    console.log('StarMapGenerator (re)render - location:', location, 'regenerate:', regenerate);

    const mapContainerRef = useRef(null);
    const [error, setError] = useState(null);
    const [celestialAttempts, setCelestialAttempts] = useState(0);
    const initAttemptRef = useRef(0);
    // Define refs at component level, not inside useEffect
    const lastDateRef = useRef(date);
    const lastLocationRef = useRef(location);
    const mountRef = useRef(false);

    // Format the date and location for display
    const formattedDate = formatDate(date);
    const formattedLocation = formatLocation(location);

    // Define generateStarMap first
    const generateStarMap = useCallback(() => {
        console.log('Generating star map...');

        if (!date || !location) {
            console.log('Cannot generate star map - missing date or location');
            return;
        }

        // Check if Celestial is available
        if (!window.Celestial || typeof window.Celestial.display !== 'function') {
            console.log('Celestial library not ready yet');
            setError('Celestial library not ready. Using simplified star map instead.');
            return;
        }

        try {
            // Parse the date - create a new Date object to ensure proper date handling
            const dateObj = new Date(date);

            // Add forceful logging of date to debug date parsing issues
            console.log('CRITICAL DATE DEBUG:', {
                rawDate: date,
                parsedDate: dateObj,
                isoString: dateObj.toISOString(),
                timestamp: dateObj.getTime(),
                validDate: !isNaN(dateObj.getTime())
            });

            // Extract location coordinates, handling both string and object formats
            let latitude, longitude;

            if (typeof location === 'string') {
                // Handle string format like "lat,lon"
                const coords = location.split(',');
                latitude = parseFloat(coords[0]);
                longitude = parseFloat(coords[1]);
                console.log('DEBUG - Parsed string location:', { original: location, latitude, longitude });
            } else {
                // Handle object format {latitude, longitude}
                latitude = parseFloat(location.latitude);
                longitude = parseFloat(location.longitude);
                console.log('DEBUG - Parsed object location:', { original: location, latitude, longitude });
            }

            // Validate coordinates
            if (isNaN(latitude) || isNaN(longitude)) {
                console.error('Invalid location coordinates:', location);
                setError('Invalid location format. Using simplified star map instead.');
                return;
            }

            // CRITICAL DEBUG INFO
            console.log('STARMAP LOCATION DEBUG:', {
                location_raw: location,
                parsed_latitude: latitude,
                parsed_longitude: longitude,
                date: dateObj
            });

            // Get container dimensions or use default
            const containerWidth = mapContainerRef.current?.clientWidth || size || 600;

            // Safety check to ensure all required values are present
            if (!dateObj || isNaN(dateObj.getTime())) {
                console.error('Invalid date object:', dateObj);
                setError('Invalid date format. Using simplified star map instead.');
                return;
            }

            if (isNaN(latitude) || isNaN(longitude)) {
                console.error('Invalid coordinates:', { latitude, longitude });
                setError('Invalid location coordinates. Using simplified star map instead.');
                return;
            }

            // Both location and date are critical for showing the correct stars at a given place and time
            // The stars visible from London vs. Moscow will be different due to different viewing angles
            // on the celestial sphere

            // Basic configuration with all the necessary settings
            const config = {
                width: containerWidth,
                height: containerWidth,
                container: "map-container",
                datapath: `${window.location.origin}/d3-celestial-data/`,
                projection: "stereographic",
                background: { fill: "#000011", stroke: "#000011", opacity: 1 },
                date: dateObj,
                center: [0, 0], // Use [0, 0] instead of null to avoid rotation errors
                follow: "zenith", // Follow the zenith of the observer
                geopos: [latitude, longitude], // Set the geographical position directly
                // The Celestial library handles converting this to celestial coordinates
                transformationMode: "equatorial", // Use equatorial mode for proper location-based view
                // Animation settings - restore animations for better user experience
                transition: true, // Enable smooth transitions
                adaptable: true, // Enable adaptative transitions
                animationDuration: 1200, // Set a reasonable duration for the animation (1.2 seconds)
                stars: {
                    show: showStars,
                    limit: 6,
                    colors: true
                },
                dsos: {
                    show: showDeepSkyObjects,
                    limit: 6
                },
                constellations: {
                    show: showConstellations,
                    names: showConstellationNames,
                    lines: showConstellations
                },
                lines: {
                    graticule: { show: showGrid, stroke: "#8989b3", width: 1, opacity: 0.6 },
                    equatorial: { show: showGrid, stroke: "#8989b3", width: 1, opacity: 0.6 },
                    ecliptic: { show: showEcliptic, stroke: "#dda74f", width: 1.5, opacity: 0.7 }
                },
                mw: { show: false },
                // Remove planets configuration entirely as it might be causing issues
                // planets: {
                //     show: true,
                //     which: ["sol", "mer", "ven", "mar", "jup", "sat"],
                //     style: { fill: "#00ccff" },
                //     labelStyle: { fill: "#00ccff" },
                //     namesType: "desig"
                // },
                interactive: true,
                form: false,
                controls: false, // This disables the zoom controls
                zoomBy: 1, // No initial zoom
            };

            // If regenerate is false, get current visibility from settings
            if (!regenerate && window.Celestial && window.Celestial.settings) {
                // Use current settings instead of props for visibility
                const settings = window.Celestial.settings;
                config.stars.show = settings.stars?.show ?? showStars;
                config.dsos.show = settings.dsos?.show ?? showDeepSkyObjects;
                // Add other similar settings
            }

            console.log('Calling Celestial.display with configuration:', config);

            // Store config reference to check actual values after render
            const configRef = { ...config };

            // Skip cleanup entirely - let Celestial handle its own updates
            // This is much faster since it prevents double rendering and preserves
            // existing DOM elements

            console.log('FINAL CONFIG BEING USED:', config);

            // Faster update approach - try to reuse existing map when possible
            try {
                // Check if we already have a rendered map that we can just update
                const mapElement = document.getElementById('map-container');
                const canvasExists = mapElement && mapElement.querySelector('canvas');

                if (canvasExists && window.Celestial.apply) {
                    // Fast update method - just update what's needed without recreating everything
                    console.log('Using fast update method for existing map');

                    try {
                        // Update position and date
                        window.Celestial.apply({
                            geopos: config.geopos,
                            date: config.date,
                            // Enable animation for smooth transitions
                            transition: true,
                            adaptable: true,
                            animationDuration: 1200
                        });

                        // Allow animated rotation rather than immediate
                        if (window.Celestial.rotate) {
                            try {
                                // Don't specify center to allow natural animation
                                window.Celestial.rotate();
                            } catch (rotateErr) {
                                console.error('Error during rotation:', rotateErr);
                                // Don't throw - continue to redraw
                            }
                        }

                        // Force redraw
                        window.Celestial.redraw();
                    } catch (applyErr) {
                        console.error('Error during fast update:', applyErr);
                        throw applyErr; // Re-throw to be caught by outer try-catch
                    }
                } else {
                    // Full redraw needed (first time or canvas missing)
                    console.log('Using full display method');

                    // Clone config to avoid reference issues
                    const safeConfig = JSON.parse(JSON.stringify({
                        ...config,
                        // Ensure date is properly handled by converting back to Date object
                        date: new Date(config.date.getTime())
                    }));

                    try {
                        window.Celestial.display(safeConfig);
                    } catch (displayErr) {
                        console.error('Error during Celestial.display call:', displayErr);
                        throw displayErr; // Re-throw to be caught by outer try-catch
                    }
                }
            } catch (err) {
                console.error('Detailed error info:', {
                    message: err.message,
                    stack: err.stack,
                    config: JSON.stringify(config, (key, value) => {
                        // Handle circular references and Date objects
                        if (key === 'date' && value instanceof Date) {
                            return value.toISOString();
                        }
                        return value;
                    })
                });
                // Fall back to simplified version
                throw new Error(`Can't display star map: ${err.message}`);
            }

            // Skip the post-render checks to save time
            setError(null);
        } catch (err) {
            console.error('Error generating star map:', err);
            setError(`Error generating star map: ${err.message}. Using simplified version instead.`);
        }
    }, [date, location, size, regenerate]);

    // Now we can use these functions in our effects
    // Update visibility settings when display options change
    useEffect(() => {
        // Only update existing map if:
        // 1. Celestial is ready
        // 2. We don't need to regenerate the map
        if (isCelestialReady()) {
            // Create configuration object just for visibility settings
            const updatedSettings = {
                stars: {
                    show: showStars
                },
                constellations: {
                    lines: showConstellations,
                    names: showConstellationNames
                },
                dsos: {
                    show: showDeepSkyObjects
                },
                lines: {
                    graticule: { show: showGrid, stroke: "#8989b3", width: 1, opacity: 0.6 },
                    equatorial: { show: showGrid, stroke: "#8989b3", width: 1, opacity: 0.6 },
                    ecliptic: { show: showEcliptic, stroke: "#dda74f", width: 1.5, opacity: 0.7 }
                }
            };

            console.log('DEBUG: Applying visibility settings:', {
                showGrid,
                showEcliptic,
                settings: updatedSettings.lines
            });

            // Apply new settings directly and redraw
            if (window.Celestial && typeof window.Celestial.apply === 'function') {
                console.log('Applying visibility settings without regenerating:', updatedSettings);
                window.Celestial.apply(updatedSettings);
                window.Celestial.redraw();

                // Debug: Check what we have after redraw
                if (window.Celestial.settings && window.Celestial.settings.lines) {
                    console.log('After redraw, lines settings are:', window.Celestial.settings.lines);
                }

                // Try direct DOM inspection as well
                setTimeout(() => {
                    const svg = document.querySelector('.starmap-canvas svg');
                    if (svg) {
                        console.log('SVG elements counts:', {
                            'gridlines': svg.querySelectorAll('path.gridline, path.graticule, path.equatorial').length,
                            'ecliptic': svg.querySelectorAll('path.ecliptic').length
                        });

                        // Force-add the grid lines and ecliptic if they don't exist
                        if (showGrid && svg.querySelectorAll('path.gridline, path.graticule, path.equatorial').length === 0) {
                            console.log('Forcing grid lines to be added');
                            forceAddGridLines(svg);
                        }

                        if (showEcliptic && svg.querySelectorAll('path.ecliptic').length === 0) {
                            console.log('Forcing ecliptic line to be added');
                            forceAddEclipticLine(svg);
                        }
                    }
                }, 500);
            }
        }
    }, [showStars, showConstellations, showConstellationNames,
        showGrid, showDeepSkyObjects, showEcliptic, regenerate]);

    // Helper function to manually add grid lines if they're missing
    const forceAddGridLines = (svg) => {
        if (!svg) return;

        // Create a simple grid pattern (meridians and parallels)
        const svgNS = "http://www.w3.org/2000/svg";

        // Create meridians (vertical lines)
        for (let i = 0; i < 24; i++) {
            const line = document.createElementNS(svgNS, "path");
            line.setAttribute("class", "graticule");
            line.setAttribute("d", `M${400 + 300 * Math.cos(i * Math.PI / 12)},${400 + 300 * Math.sin(i * Math.PI / 12)} A300,300 0 0,1 ${400 + 300 * Math.cos((i + 12) * Math.PI / 12)},${400 + 300 * Math.sin((i + 12) * Math.PI / 12)}`);
            line.setAttribute("stroke", "#8989b3");
            line.setAttribute("stroke-width", "1");
            line.setAttribute("opacity", "0.6");
            line.setAttribute("fill", "none");
            svg.appendChild(line);
        }

        // Create parallels (horizontal lines)
        for (let i = 1; i < 6; i++) {
            const radius = 300 * (i / 6);
            const circle = document.createElementNS(svgNS, "circle");
            circle.setAttribute("class", "graticule");
            circle.setAttribute("cx", "400");
            circle.setAttribute("cy", "400");
            circle.setAttribute("r", radius.toString());
            circle.setAttribute("stroke", "#8989b3");
            circle.setAttribute("stroke-width", "1");
            line.setAttribute("opacity", "0.6");
            circle.setAttribute("fill", "none");
            svg.appendChild(circle);
        }
    };

    // Helper function to manually add ecliptic line if it's missing
    const forceAddEclipticLine = (svg) => {
        if (!svg) return;

        const svgNS = "http://www.w3.org/2000/svg";
        const ecliptic = document.createElementNS(svgNS, "ellipse");
        ecliptic.setAttribute("class", "ecliptic");
        ecliptic.setAttribute("cx", "400");
        ecliptic.setAttribute("cy", "400");
        ecliptic.setAttribute("rx", "350");
        ecliptic.setAttribute("ry", "330");
        ecliptic.setAttribute("transform", "rotate(23.5 400 400)"); // Ecliptic is tilted at 23.5 degrees
        ecliptic.setAttribute("stroke", "#dda74f");
        ecliptic.setAttribute("stroke-width", "1.5");
        ecliptic.setAttribute("opacity", "0.7");
        ecliptic.setAttribute("fill", "none");
        svg.appendChild(ecliptic);
    };

    // Initialize Celestial with default settings
    useEffect(() => {
        console.log('Init effect with default settings');
        if (isCelestialReady()) {
            initializeStarMap();
        }
    }, []); // Run once on mount

    // Optimize the initializeStarMap function
    const initializeStarMap = useCallback(() => {
        console.log('Initializing star map...');

        if (!window.d3) {
            console.error('d3 not found');
            setError('Required libraries not loaded. Using simplified star map instead.');
            return false;
        }

        if (!window.Celestial) {
            console.error('Celestial library not found');
            setError('Celestial library not loaded. Using simplified star map instead.');
            return false;
        }

        if (!mapContainerRef.current) {
            console.error('Map container ref not available');
            return false;
        }

        try {
            // Skip aggressive cleanup to preserve DOM elements - faster reload
            // We'll let Celestial handle its own cleanup during display()

            // Only clear if we detect a problem with the container
            if (!mapContainerRef.current.querySelector('#map-container')) {
                console.log('Map container missing, doing minimal cleanup');
                // Just ensure container is ready for new content without full clearing
                if (mapContainerRef.current.childNodes.length > 3) {
                    // Only clear if there are too many elements (possible DOM leak)
                    mapContainerRef.current.innerHTML = '';
                }
            }

            // Check if the map container div already exists
            let mapElement = document.getElementById('map-container');

            // Only create a new container if it doesn't exist
            if (!mapElement) {
                mapElement = document.createElement('div');
                mapElement.id = 'map-container';
                mapElement.style.width = '100%';
                mapElement.style.height = '100%';
                mapElement.style.position = 'absolute';
                mapElement.style.top = '0';
                mapElement.style.left = '0';
                mapContainerRef.current.appendChild(mapElement);
            }

            console.log('Map container prepared, generating map');

            // Generate the star map
            generateStarMap();
            return true;
        } catch (err) {
            console.error('Error during map initialization:', err);
            setError('Error initializing star map. Using simplified version instead.');
            return false;
        }
    }, [generateStarMap]);

    // Listen for celestial-ready event
    useEffect(() => {
        const handleCelestialReady = () => {
            console.log('Received celestial-ready event');
            if (initializeStarMap()) {
                console.log('Star map initialized successfully after celestial-ready event');
            }
        };

        // Listen for the custom event
        window.addEventListener('celestial-ready', handleCelestialReady);

        // Also check if celestial is already loaded - But ONLY on first mount
        if (isCelestialReady() && !mountRef.current) {
            console.log('Celestial already loaded on mount, initializing');
            initializeStarMap();
            mountRef.current = true; // Mark as mounted
        }

        return () => {
            window.removeEventListener('celestial-ready', handleCelestialReady);
        };
    }, []); // <-- EMPTY dependency array to ensure it only runs once on mount

    // Listen for data loaded event
    useEffect(() => {
        const handleDataLoaded = () => {
            console.log('Received celestial-data-loaded event');
            // Wait a short moment to ensure everything is ready
            setTimeout(() => {
                if (isCelestialReady()) {
                    console.log('Initializing after data loaded event');
                    initializeStarMap();
                }
            }, 300);
        };

        window.addEventListener('celestial-data-loaded', handleDataLoaded);

        return () => {
            window.removeEventListener('celestial-data-loaded', handleDataLoaded);
        };
    }, [initializeStarMap]);

    // Optimize the useEffect for changes in date and location - with fast updates
    useEffect(() => {
        if (date && location && isCelestialReady()) {
            // Check if we need to update
            const locationChanged = lastLocationRef.current !== location;
            const dateChanged = lastDateRef.current !== date;

            // Add explicit date debugging
            console.log('Date change check:', {
                previousDate: lastDateRef.current,
                newDate: date,
                dateChanged,
                dateObj: new Date(date),
                previousDateObj: lastDateRef.current ? new Date(lastDateRef.current) : null
            });

            if (regenerate || locationChanged || dateChanged) {
                console.log('Map needs to update due to:', {
                    regenerate,
                    locationChanged,
                    dateChanged
                });

                // Update references
                lastDateRef.current = date;
                lastLocationRef.current = location;

                // ALWAYS do a complete rebuild for date changes
                if (dateChanged) {
                    console.log('DATE CHANGED - Updating map time without rotation');

                    try {
                        // Parse date and location
                        const dateObj = new Date(date);

                        // For date-only changes, just update the date without rotating
                        if (!locationChanged && window.Celestial && window.Celestial.apply) {
                            console.log('Performing quiet date update without rotation');
                            // Just update the date without changing the view
                            window.Celestial.apply({
                                date: dateObj,
                                // Disable animations for date-only changes
                                transition: false,
                                adaptable: false,
                                animationDuration: 0
                            });

                            // Redraw without rotation
                            window.Celestial.redraw();

                            // Date-only changes don't need rotation at all
                            return;
                        }

                        // If location also changed, perform a full update
                        let latitude, longitude;

                        if (typeof location === 'string') {
                            const coords = location.split(',');
                            latitude = parseFloat(coords[0]);
                            longitude = parseFloat(coords[1]);
                        } else {
                            latitude = parseFloat(location.latitude);
                            longitude = parseFloat(location.longitude);
                        }

                        // Create a minimal configuration with only essential settings
                        const basicConfig = {
                            width: mapContainerRef.current?.clientWidth || 600,
                            height: mapContainerRef.current?.clientWidth || 600,
                            container: "map-container",
                            projection: "stereographic",
                            transform: "equatorial",
                            datapath: `${window.location.origin}/d3-celestial-data/`,
                            background: { fill: "#000011", stroke: "#000011", opacity: 1 },
                            stars: {
                                show: true,
                                limit: 6,
                                colors: true
                            },
                            dsos: {
                                show: true
                            },
                            mw: {
                                show: false
                            },
                            constellations: {
                                show: true,
                                names: true,
                                lines: true,
                                bounds: false
                            },
                            lines: {
                                graticule: { show: showGrid, stroke: "#8989b3", width: 1, opacity: 0.6 },
                                equatorial: { show: showGrid, stroke: "#8989b3", width: 1, opacity: 0.6 },
                                ecliptic: { show: showEcliptic, stroke: "#dda74f", width: 1.5, opacity: 0.7 }
                            },
                            planets: {
                                show: true
                            },
                            center: [0, 0],
                            follow: "zenith",
                            geopos: [latitude, longitude],
                            date: dateObj
                        };

                        // Force complete rebuild
                        console.log('Using direct Celestial.display call with simple config for date change');
                        window.Celestial.display(basicConfig);
                    } catch (err) {
                        console.error('Date change error:', err);
                        setError(`Error with date change: ${err.message}`);
                    }
                }
                // Force a full rebuild when date changes to ensure proper updates
                else if (regenerate || locationChanged) {
                    // Full rebuild needed for regenerate flag or location change
                    console.log('Doing complete rebuild due to regenerate flag or location change');
                    initializeStarMap();
                }
                // Add back the section for location-only updates
                else if (document.getElementById('map-container') &&
                    document.querySelector('#map-container canvas') &&
                    window.Celestial &&
                    window.Celestial.apply) {
                    // Fast partial update - extract coordinates
                    let latitude, longitude;

                    try {
                        if (typeof location === 'string') {
                            const coords = location.split(',');
                            latitude = parseFloat(coords[0]);
                            longitude = parseFloat(coords[1]);
                        } else {
                            latitude = parseFloat(location.latitude);
                            longitude = parseFloat(location.longitude);
                        }

                        // For location changes only - do fast direct update
                        console.log('Performing fast update for location change only');
                        window.Celestial.apply({
                            geopos: [latitude, longitude],
                            date: new Date(date),
                            // Ensure these settings are passed in location-only changes as well
                            lines: {
                                graticule: { show: showGrid, stroke: "#8989b3", width: 1, opacity: 0.6 },
                                equatorial: { show: showGrid, stroke: "#8989b3", width: 1, opacity: 0.6 },
                                ecliptic: { show: showEcliptic, stroke: "#dda74f", width: 1.5, opacity: 0.7 }
                            }
                        });
                        window.Celestial.redraw();
                    } catch (e) {
                        console.error('Fast update failed, falling back to full rebuild:', e);
                        initializeStarMap();
                    }
                } else {
                    // Canvas not available or other issues - do full rebuild
                    initializeStarMap();
                }
            }
        }
    }, [date, location, size, regenerate, initializeStarMap]);

    // Check periodically for celestial to be loaded
    useEffect(() => {
        if (!isCelestialReady() && celestialAttempts < 15) {
            const timer = setTimeout(() => {
                setCelestialAttempts(prev => prev + 1);

                if (isCelestialReady()) {
                    console.log(`Celestial library detected on attempt ${celestialAttempts + 1}`);
                    initializeStarMap();
                } else if (celestialAttempts >= 5) {
                    // After a few attempts, use increasing delays
                    console.log(`Celestial library not found on attempt ${celestialAttempts + 1}, will retry...`);
                    if (celestialAttempts === 10) {
                        setError('Still loading celestial library. Using simplified star map for now.');
                    }
                }
            }, 500 + celestialAttempts * 200); // Increasing delay with each attempt

            return () => clearTimeout(timer);
        }
    }, [celestialAttempts, initializeStarMap]);

    // We need a direct effect to initialize the map on first render
    useEffect(() => {
        console.log('Initial useEffect for map generation');
        // Try to initialize the map if Celestial is ready
        if (isCelestialReady()) {
            console.log('Celestial is ready on initial render, generating map');
            initializeStarMap();
        }
    }, [initializeStarMap]); // Only run once on component mount with stable initializeStarMap

    // Set error and notify parent
    useEffect(() => {
        if (error && typeof onError === 'function') {
            onError(error);
        }
    }, [error, onError]);

    // Notify parent when map is successfully generated
    const notifyMapLoaded = useCallback(() => {
        if (typeof onLoaded === 'function') {
            // Check if this was a date-only change
            const isDateOnlyChange = lastLocationRef.current === location &&
                lastDateRef.current !== date;

            // For date-only changes, don't wait for animations (there aren't any)
            if (isDateOnlyChange) {
                console.log('Date-only change completed - notifying immediately');
                onLoaded();
                // Fire the animation complete event right away
                setTimeout(() => {
                    window.dispatchEvent(new CustomEvent('starmap-animation-complete'));
                }, 100);
                return;
            }

            // For location changes or initial load, use the normal animation timing
            const initialDelay = 200; // Short delay to let the map become visible
            setTimeout(() => {
                // At this point, the map is visible but still animating
                // Notify parent to update loading state to 100%
                onLoaded();

                // After the animation completes, hide the loading indicator completely
                const animationDuration = 1500; // Slightly longer than the transition time
                setTimeout(() => {
                    // Fire a custom event to signal the animation is complete
                    window.dispatchEvent(new CustomEvent('starmap-animation-complete'));
                }, animationDuration);
            }, initialDelay);
        }
    }, [onLoaded, date, location, lastDateRef, lastLocationRef]);

    // Call notifyMapLoaded when map is successfully initialized
    useEffect(() => {
        if (isCelestialReady() && mapContainerRef.current?.querySelector('canvas') && !error) {
            const timer = setTimeout(() => {
                notifyMapLoaded();
            }, 300); // Small delay to ensure map is fully rendered
            return () => clearTimeout(timer);
        }
    }, [notifyMapLoaded, error]);

    // Function to capture the star map as an image
    const captureStarMap = () => {
        try {
            const mapCanvas = document.querySelector('#celestial-map-container canvas');
            if (!mapCanvas) {
                console.error('No canvas element found');
                return null;
            }

            // Create a new canvas with proper dimensions to include caption
            const fullCanvas = document.createElement('canvas');
            const ctx = fullCanvas.getContext('2d');

            // Determine if we need to add space for captions
            const hasCaptions = caption || formattedDate || formattedLocation;

            // Set canvas size to match the star map
            fullCanvas.width = mapCanvas.width;
            fullCanvas.height = hasCaptions ? mapCanvas.height + 100 : mapCanvas.height;

            // Draw the star map
            ctx.fillStyle = '#000000';
            ctx.fillRect(0, 0, fullCanvas.width, fullCanvas.height);
            ctx.drawImage(mapCanvas, 0, 0);

            // Add captions if needed
            if (hasCaptions) {
                ctx.fillStyle = '#ffffff';
                ctx.font = 'bold 20px Arial';
                ctx.textAlign = 'center';

                // Add main caption
                if (caption) {
                    ctx.fillText(caption, fullCanvas.width / 2, mapCanvas.height + 30);
                }

                // Add date and location
                if (formattedDate || formattedLocation) {
                    ctx.font = '16px Arial';
                    const locationText = `${formattedDate}${formattedDate && formattedLocation ? ' • ' : ''}${formattedLocation}`;
                    ctx.fillText(locationText, fullCanvas.width / 2, mapCanvas.height + 60);
                }
            }

            // Return the image as data URL
            return fullCanvas.toDataURL('image/png');
        } catch (error) {
            console.error('Error capturing star map:', error);
            return null;
        }
    };

    // Expose the capture function to the parent component
    React.useImperativeHandle(
        ref,
        () => ({
            captureStarMap
        }),
        [captureStarMap, caption, formattedDate, formattedLocation]
    );

    // Add CSS to hide zoom controls
    useEffect(() => {
        // Add a style to hide zoom controls and fix SVG object positioning
        const style = document.createElement('style');
        style.textContent = `
            #celestial-zoomin, #celestial-zoomout {
                display: none !important;
                visibility: hidden !important;
            }
            
            /* Ensure SVG objects properly fit their containers */
            object[type="image/svg+xml"] {
                width: 100%;
                height: 100%;
                display: block;
            }
            
            /* Ensure proper rendering of orbital decorations */
            .orbital-decoration-wrapper {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                z-index: 100;
                pointer-events: none;
                display: flex;
                align-items: center;
                justify-content: center;
                overflow: visible;
                padding: 24px;
            }
            
            /* Ensure the SVG inside scales properly */
            .orbital-decoration-wrapper object {
                width: 100%;
                height: 100%;
                display: block;
                object-fit: contain;
                max-width: 100%;
                max-height: 100%;
            }

            /* Ensure SVG objects properly fit their containers */
            .orbital-decoration-wrapper img {
                width: 100%;
                height: 100%;
                display: block;
                object-fit: cover;
                max-width: 100%;
                max-height: 100%;
                image-rendering: auto;
                filter: drop-shadow(0 0 2px rgba(255,255,255,0.1));
                
            }
            
            /* Force grid and ecliptic lines to always be visible */
            svg path.graticule, 
            svg path.gridline, 
            svg path.equatorial {
                stroke: #8989b3 !important;
                stroke-width: 1px !important;
                opacity: 0.6 !important;
                display: inline !important;
                visibility: visible !important;
            }
            
            svg path.ecliptic {
                stroke: #dda74f !important;
                stroke-width: 1.5px !important;
                opacity: 0.7 !important;
                display: inline !important;
                visibility: visible !important;
            }
        `;
        document.head.appendChild(style);

        // Clean up
        return () => {
            document.head.removeChild(style);
        };
    }, []);

    return (
        <div
            className="starmap-wrapper"
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                position: 'relative',
                padding: '0',
                overflow: 'visible',
                gap: '30px' /* Use gap for consistent spacing between elements */
            }}
        >
            <StarMapContainer>
                <MapFrame>
                    <MapCanvas ref={mapContainerRef} />
                </MapFrame>

                {/* Show fallback map if there's an error */}
                {error && (
                    <div className="starmap-fallback-container" style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '0',
                        paddingBottom: '100%',
                        zIndex: 1,
                        borderRadius: '50%',
                        overflow: 'hidden'
                    }}>
                        <FallbackStarMap
                            date={date}
                            location={location}
                            caption={caption}
                            style={{ position: 'absolute', width: '100%', height: '100%' }}
                        />
                    </div>
                )}

                {/* Show error message */}
                {error && <ErrorMessage>{error}</ErrorMessage>}
            </StarMapContainer>

            {/* Show caption if provided - with explicit position to ensure visibility */}
            {(caption || formattedLocation || formattedDate) && !error && (
                <CaptionSection>
                    {caption && <MainCaption>{caption}</MainCaption>}
                    <LocationDate>
                        {formattedLocation && formattedDate
                            ? `${formattedLocation} • ${formattedDate}`
                            : formattedLocation || formattedDate}
                    </LocationDate>
                </CaptionSection>
            )}
        </div>
    );
});

// Export with memo to prevent unnecessary re-renders
export default memo(StarMapGenerator); 